import { FC, PropsWithChildren } from 'react';
import { keyframes, styled } from '@mui/material/styles';
import { themePalette } from '@theme/muiTheme';

type Props = PropsWithChildren<{
  inside?: boolean;
  open?: boolean;
}>;

export const LOADER_Z_INDEX = 1400;

const SIZE = 80;

const VIEW_BOX_SIZE = 44;
const THICKNESS = 2;
const RADIUS = (VIEW_BOX_SIZE - THICKNESS) / 2;

const spin = keyframes({
  '0%': { transform: 'rotate(0deg)' },
  '100%': { transform: 'rotate(360deg)' },
});

const Spinner = styled('div')({
  width: SIZE,
  height: SIZE,
  animation: `${spin} 2s linear infinite`,
  willChange: 'transform',
});

const Centered = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
});

const Backdrop = styled(Centered)({
  backgroundColor: themePalette.grey[1000],
  opacity: '0.8 !important',
  zIndex: LOADER_Z_INDEX,
  width: '100dvw',
  height: '100dvh',
  position: 'fixed',
  left: 0,
  top: 0,
});

const InlineBackdrop = styled(Centered)({
  width: '100%',
  height: '100%',
  zIndex: 10,
  opacity: '0.8 !important',
  backgroundColor: themePalette.grey[500],
});

const Arc = () => (
  <Spinner role="progressbar" aria-label="Loading">
    <svg
      width={SIZE}
      height={SIZE}
      aria-hidden
      viewBox={`${VIEW_BOX_SIZE / 2} ${
        VIEW_BOX_SIZE / 2
      } ${VIEW_BOX_SIZE} ${VIEW_BOX_SIZE}`}
    >
      <circle
        cx={VIEW_BOX_SIZE}
        cy={VIEW_BOX_SIZE}
        r={RADIUS}
        fill="none"
        stroke={themePalette.primary.main}
        strokeWidth={THICKNESS}
        strokeDasharray="100 200"
        strokeDashoffset="-15"
      />
    </svg>
  </Spinner>
);

export const CustomLoader: FC<Props> = ({ children, inside, open }) => {
  if (inside) {
    return (
      <InlineBackdrop>
        {children}
        <Arc />
      </InlineBackdrop>
    );
  }

  if (!open) return null;

  return (
    <Backdrop>
      {children}
      <Arc />
    </Backdrop>
  );
};
